import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import lang from '../../../../lib/language';
import { Earning } from '../../../../lib/models/fundamental/types';
import { getEarningsSurpriseColorState } from '../../../../lib/store/fundamental/helpers';
import { formatNumberWithSuffix, getLimitedText, pluralizeByCount } from '../../../../lib/util/DataHelpers';
import { earningsCalendarInitialDate } from '../../../../lib/util/DateTimeHelpers';
import { INVALID_VALUE } from '../../../../lib/util/types';

import ChartIcon from '../../../../assets/img/chart-icon.png';
import ClearIcon from '../../../../assets/img/close-circle.svg';
import ChevronDown from '../../../../assets/img/icon_expan_down_ratios.svg';
import SearchAreaIcon from '../../../../assets/img/search-area.svg';
import BaseTable from '../../../../components/BaseTable/BaseTable';
import CompanyLogo from '../../../../components/CompanyLogo/CompanyLogo';
import Button from '../../../Button/Button';
import { ButtonTypesEnum } from '../../../Button/Button.types';
import ClickableDiv from '../../../ClickableDiv/ClickableDiv';
import ClickableImage from '../../../ClickableImage/ClickableImage';
import CommonDropdown from '../../../CommonDropdown/CommonDropdown';
import DateRangePickerWrapper from '../../../DesktopCalendar/DateRangePickerWrapper';
import useBrowserWidth from '../../../hooks/useBrowserWidth';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileFromToDatePicker from '../../../MobileCalendar/MobileFromToDatePicker';
import {
  EARNINGS_TABLE_HEADERS,
  EarningsSymbolFilterEnum,
} from '../../EarningsCalendar.constants';
import {
  formatDate,
  formatEarningsCurrency,
  formatEarningsRowDateLabel,
  getFormattedSurpiseEpsWithSign,
  groupByDate,
  SymbolFilterOption,
} from '../../earningsCalendar.helpers';
import EarningsStatusBadge from '../EarningsStatusBadge/EarningsStatusBadge';

import { EarningsCalendarRow, EarningsTableProps } from './EarningsTable.types';
import SkeletonLoaderRow from './SkeletonLoaderRow';

import styles from './EarningsTable.module.scss';

const NARROW_WIDTH = 850;
const NARROW_NAME_LIMIT = 6;
const WIDE_NAME_LIMIT = 13;
const SKELETON_ROW_KEYS = ['first', 'second', 'third', 'fourth', 'fifth'];

const EarningsTable: FC<EarningsTableProps> = ({
  rows,
  totalElements,
  range,
  symbolFilterOptions,
  selectedSymbolFilter,
  activeSymbols,
  searchValue,
  isPending,
  showSkeleton,
  canLoadMore,
  isDiscoverScreen,
  onDateChange,
  onSymbolFilterSelect,
  onClearSymbolFilter,
  onOpenSymbolSearch,
  onLoadMore,
  onEarningSelect,
}: EarningsTableProps): JSX.Element => {
  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();
  const [separatedContentByDate, setSeparatedContentByDate] = useState<[string, EarningsCalendarRow[]][]>([]);

  const nameLimit = browserWidthSize <= NARROW_WIDTH ? NARROW_NAME_LIMIT : WIDE_NAME_LIMIT;
  const searchedText = searchValue.trim();
  const hasActiveSymbolFilter = activeSymbols.length > 0;
  const selectedStartDateState = range.startDate ? formatDate(range.startDate) : undefined;
  const selectedEndDateState = range.endDate ? formatDate(range.endDate) : undefined;

  const rowsByEarning = useMemo(() => new Map(rows.map(row => [row.earning, row])), [rows]);

  useEffect(() => {
    const grouped = groupByDate(rows.map(row => row.earning));
    setSeparatedContentByDate(
      grouped.map(([date, earnings]) => [date, earnings.map(e => rowsByEarning.get(e) as EarningsCalendarRow)]),
    );
  }, [rows, rowsByEarning]);

  const hasSymbolFilter = Boolean(selectedSymbolFilter && symbolFilterOptions);
  const isSingleSymbolFilterApplied = selectedSymbolFilter?.id === EarningsSymbolFilterEnum.BySymbol
    && activeSymbols.length === 1;

  const renderSymbolFilter = () => (!hasSymbolFilter ? null : (
    <div className={styles.symbolFilterDropdownWrapper}>
      {isSingleSymbolFilterApplied ? (
        <ClickableDiv className={styles.symbolFilterChip} onClick={() => onOpenSymbolSearch?.()}>
          <div className={GlobalClassesEnum.CommonBody1Text}>{activeSymbols[0]}</div>
          <ClickableImage
            src={ClearIcon}
            className={styles.symbolFilterChipClearIcon}
            onClick={(event) => {
              event.stopPropagation();
              onClearSymbolFilter?.();
            }}
          />
        </ClickableDiv>
      ) : (
        <CommonDropdown
          options={symbolFilterOptions as SymbolFilterOption[]}
          selectedOption={selectedSymbolFilter as SymbolFilterOption}
          onSelectOption={option => onSymbolFilterSelect?.(option)}
          title={lang.commonDiscoverCalendarFilterTitle()}
          menuClassName={styles.symbolFilterMenu}
          itemClassName={styles.symbolFilterMenuItem}
          chevronIcon={ChevronDown}
        />
      )}
    </div>
  ));

  const renderEpsData = (
    epsEst: string,
    currency: string,
    symbol: string,
    surprisePercent: string,
    surpriseEps: string,
  ) => {
    if (surprisePercent !== '' && surpriseEps !== '') {
      const formattedEps = formatEarningsCurrency(surpriseEps, currency, symbol);
      const epsWithSign = getFormattedSurpiseEpsWithSign(surpriseEps, formattedEps);
      const surpriseColorState = getEarningsSurpriseColorState(surpriseEps);
      const surpriseColorClass = surpriseColorState ? styles[surpriseColorState] : undefined;
      const formattedSurpPercent = formatNumberWithSuffix(Math.abs(parseFloat(surprisePercent)), '%');

      return (
        <span className={classNames(styles.surpriseWrapper, surpriseColorClass)}>
          <span className={styles.surpriseEps}>{epsWithSign}</span>
          <span className={styles.surprisePercent}>({formattedSurpPercent})</span>
        </span>
      );
    }

    const formattedEps = epsEst ? formatEarningsCurrency(epsEst, currency, symbol) : INVALID_VALUE;

    return (
      <span className={styles.epsWrapper}>
        <span className={styles.eps}>{formattedEps}</span>
        {epsEst && (
          <span className={styles.greyEstText}>({lang.commonDiscoverCalendarEarningsEst()})</span>
        )}
      </span>
    );
  };

  const renderMobileEpsData = (item: Earning) => {
    const { epsEst, currency, symbol, epsSurprisePercent, epsSurprise } = item;

    // The projections response omits these fields for rows without report data, so
    // they can be undefined at runtime despite the string type - anything missing
    // must fall through to the "EPS est. -" rendering below.
    if (epsSurprisePercent && epsSurprise) {
      const formattedEps = formatEarningsCurrency(epsSurprise, currency, symbol);
      const epsWithSign = getFormattedSurpiseEpsWithSign(epsSurprise, formattedEps);
      const surpriseColorState = getEarningsSurpriseColorState(epsSurprise);
      const surpriseColorClass = surpriseColorState ? styles[surpriseColorState] : undefined;
      const formattedSurpPercent = formatNumberWithSuffix(Math.abs(parseFloat(epsSurprisePercent)), '%');

      return (
        <div className={styles.mobileEpsRow}>
          <span className={styles.mobileEpsLabel}>{lang.headerEarningsEPS()}</span>
          <span className={classNames(styles.surpriseWrapper, surpriseColorClass)}>
            <span className={styles.surpriseEps}>{epsWithSign}</span>
            <span className={styles.surprisePercent}>({formattedSurpPercent})</span>
          </span>
        </div>
      );
    }

    const formattedEpsEst = epsEst ? formatEarningsCurrency(epsEst, currency, symbol) : INVALID_VALUE;

    return (
      <div className={styles.mobileEpsRow}>
        <span className={styles.mobileEpsLabel}>{lang.headerEarningsCalendarEarningsEPSEstimateMobile()}</span>
        <span className={styles.mobileEpsEstValue}>{formattedEpsEst}</span>
      </div>
    );
  };

  const tableHeaders = EARNINGS_TABLE_HEADERS.map((item: string) => <th key={item}>{item}</th>);

  const tableItems = rows.map(({ earning, logo, logoUrl, companyName }) => (
    <tr key={`${earning.time} ${earning.symbol}`} onClick={() => onEarningSelect(earning)}>
      <td>
        <div className={styles.wrapperCompany}>
          {/* Rendered unconditionally: CompanyLogo falls back to the default
              bubble itself when the symbol has no logo data at all. */}
          <CompanyLogo url={logoUrl || ''} logo={logo || ''} symbol={earning.symbol} />
          <div className={styles.companyInfo}>
            <div className={styles.symbol}>{earning.symbol}</div>
            <div className={styles.name}>{getLimitedText(companyName, nameLimit)}</div>
          </div>
        </div>
      </td>
      <td>{formatEarningsRowDateLabel(earning.date)}</td>
      <td><EarningsStatusBadge time={earning.time} reportStatus={earning.reportStatus} /></td>
      <td>
        {renderEpsData(
          earning.epsEst,
          earning.currency,
          earning.symbol,
          earning.epsSurprisePercent,
          earning.epsSurprise,
        )}
      </td>
    </tr>
  ));

  const renderLoadMore = (): JSX.Element => {
    if (!canLoadMore) return <></>;

    return (
      <Button className={styles.btnLoadMore} variant={ButtonTypesEnum.Discover} onClick={onLoadMore}>
        {lang.commonLoadMore()}
      </Button>
    );
  };

  const renderSkeletonLoader = () => (
    <div className={styles.tableWrapper}>
      <BaseTable isDiscoverTable>
        {!isMobile && (
          <thead>
            <tr>{tableHeaders}</tr>
          </thead>
        )}
        {SKELETON_ROW_KEYS.map(key => <SkeletonLoaderRow key={key} />)}
      </BaseTable>
    </div>
  );

  const renderNoData = () => {
    if (!hasActiveSymbolFilter) {
      return (
        <div className={styles.noDataPeriodWrapper}>
          <img className={styles.noDataPeriodIcon} src={ChartIcon} alt="chart icon" />
          <p className={styles.noDataPeriodText}>{lang.upcomingEarningsNoDataForPeriod()}</p>
        </div>
      );
    }

    return (
      <div className={styles.noContentWrapper}>
        <img className={styles.noSearchResultsIcon} src={SearchAreaIcon} alt="search area icon" />
        <p className={styles.noContentText}>
          {lang.commonNoResultsFoundFor(searchedText || selectedSymbolFilter?.label || '')}
        </p>
      </div>
    );
  };

  const renderTable = () => (rows.length ? (
    <div className={classNames(
      { [styles.tableWrapper]: !isMobile },
      { [GlobalClassesEnum.CommonContainer]: !isMobile },
      { [styles.earningsTableWrapper]: isMobile },
    )}
    >
      <BaseTable isDiscoverTable>
        {!isMobile && (
          <thead>
            <tr>{tableHeaders}</tr>
          </thead>
        )}
        <tbody>{tableItems}</tbody>
      </BaseTable>
      {!isPending && renderLoadMore()}
    </div>
  ) : renderNoData());

  const datePickerInitialState = {
    startDate: range.startDate ? new Date(range.startDate) : earningsCalendarInitialDate(),
    endDate: range.endDate ? new Date(range.endDate) : earningsCalendarInitialDate(),
  };

  return (
    <>
      <div className={styles.wrapperDateRangerPicker}>
        {isMobile && (
          <div
            className={classNames(
              !isDiscoverScreen ? styles.datePickerWrapperMob : undefined,
              styles.datePickerWrapperWidth,
            )}
          >
            {!isDiscoverScreen && (
              <div className={styles.lengthEventsMobile}>
                <span>{totalElements}</span>
                <span>
                  {pluralizeByCount(
                    totalElements,
                    lang.commonDiscoverCalenderItem(),
                    lang.commonDiscoverCalenderItems(),
                  )}
                </span>
              </div>
            )}
            <div className={styles.epsEstAndDate}>
              <div className={styles.mobileLeftFilters}>
                <MobileFromToDatePicker
                  label={lang.commonDiscoverCalenderEventsToday()}
                  initialState={datePickerInitialState}
                  minDateState={selectedStartDateState}
                  minDateCalendar={earningsCalendarInitialDate()}
                  maxDateState={selectedEndDateState}
                  onDateSelect={onDateChange}
                  withDateRangeDropdown
                />
                {renderSymbolFilter()}
              </div>
            </div>
          </div>
        )}
        {!isMobile && (
          <div className={styles.desktopFiltersWrapper}>
            <div className={styles.desktopLeftFilters}>
              <DateRangePickerWrapper
                initialState={datePickerInitialState}
                minDateState={selectedStartDateState}
                minDateCalendar={earningsCalendarInitialDate()}
                maxDateState={selectedEndDateState}
                onDateSelect={onDateChange}
                withDateRangeDropdown
              />
              {renderSymbolFilter()}
            </div>
          </div>
        )}
      </div>

      {!isMobile && (showSkeleton ? renderSkeletonLoader() : renderTable())}

      {isMobile && (showSkeleton ? renderSkeletonLoader() : (
        <>
          {!rows.length ? renderNoData() : (
            <div className={styles.mobileTableWrapper}>
              {separatedContentByDate.map(([date, dateRows]) => (
                <Fragment key={date}>
                  <div className={styles.mobileDate}>{formatEarningsRowDateLabel(date)}</div>
                  <div className={styles.mobileDateContainer}>
                    {dateRows.map(({ earning, logo, logoUrl, companyName }) => (
                      <ClickableDiv
                        key={earning.id}
                        className={styles.mobileRow}
                        onClick={() => onEarningSelect(earning)}
                      >
                        <CompanyLogo url={logoUrl || ''} logo={logo || ''} symbol={earning.symbol} />
                        <div className={styles.mobileInfoWrapper}>
                          <div className={styles.mobileMainInfo}>
                            <div className={styles.mobileSymbol}>{earning.symbol}</div>
                            {renderMobileEpsData(earning)}
                          </div>
                          <div className={styles.mobileSubInfo}>
                            <div className={styles.mobileName}>{companyName}</div>
                            <EarningsStatusBadge
                              time={earning.time}
                              reportStatus={earning.reportStatus}
                            />
                          </div>
                        </div>
                      </ClickableDiv>
                    ))}
                  </div>
                </Fragment>
              ))}
              {renderLoadMore()}
            </div>
          )}
        </>
      ))}
    </>
  );
};

export default EarningsTable;
